<template>
  <div class="pay-plan-small-card" :class="`pay-plan-small-card--${variant}`">
    <button
      v-if="hasPastRail"
      type="button"
      class="pay-plan-small-card__arrow pay-plan-small-card__arrow--past"
      aria-label="Previous pay plan version"
      @click.stop="$emit('previous-click', $event)"
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path
          d="M10 12 6 8l4-4"
          stroke="#344054"
          stroke-width="1.33"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>

    <div
      v-if="isAddButtonVariant && blocked"
      class="pay-plan-small-card__add pay-plan-small-card__add--blocked"
    >
      <NotAllowedReason
        :title="blocked.title"
        :description="blocked.description"
        :target-id="blocked.versionId"
        @open="emit('blocked-open', blocked)"
      />
    </div>

    <div
      v-else-if="isAddButtonVariant"
      class="pay-plan-small-card__add"
    >
      <button
        type="button"
        class="pay-plan-small-card__add-btn"
        @click.stop="$emit('add-click', $event)"
      >
        <span class="pay-plan-small-card__add-icon" aria-hidden="true">
          <img :src="plusIcon" alt="" />
        </span>
      </button>
      <span class="pay-plan-small-card__add-label">{{ addLabel }}</span>
    </div>

    <div
      v-else-if="isUpcomingLabelVariant"
      class="pay-plan-small-card__add pay-plan-small-card__add--label-only"
    >
      <span class="pay-plan-small-card__add-label">{{ addLabel }}</span>
    </div>

    <div
      v-else
      class="pay-plan-small-card__plan"
      :class="`pay-plan-small-card__plan--${state}`"
      role="button"
      tabindex="0"
      @click="$emit('plan-click', $event)"
      @keydown.enter.prevent="$emit('plan-click', $event)"
    >
      <div class="pay-plan-small-card__plan-top">
        <span class="pay-plan-small-card__amount">{{ amountFormatted }}</span>
        <span class="pay-plan-small-card__basis">{{ basisLabel }}</span>
        <span class="pay-plan-small-card__spacer" aria-hidden="true" />
        <button
          v-if="showClose"
          type="button"
          class="pay-plan-small-card__close"
          aria-label="Remove pay plan"
          @click.stop="$emit('close-click', $event)"
        >
          <img :src="xCloseIcon" alt="" />
        </button>
      </div>
      <div class="pay-plan-small-card__dates-row">
        <span
          v-if="versionLabel"
          class="pay-plan-small-card__version"
        >{{ versionLabel }}</span>
        <p class="pay-plan-small-card__dates">{{ dateText }}</p>
      </div>
    </div>

    <button
      v-if="hasFutureRail"
      type="button"
      class="pay-plan-small-card__arrow pay-plan-small-card__arrow--future"
      aria-label="Next pay plan version"
      @click.stop="$emit('next-click', $event)"
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path
          d="M6 12l4-4-4-4"
          stroke="#344054"
          stroke-width="1.33"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>
  </div>
</template>

<script setup>
import { computed } from "vue";

import plusIcon from "./icons/plus-white.svg";
import xCloseIcon from "@components/assets/x-close.svg";
import NotAllowedReason from "@components/NotAllowedReason/NotAllowedReason.vue";

const ADD_LABELS = {
  "no-current": "Add First Pay Plan",
  "past-no-current": "Add new Pay Plan",
  "no-current-future": "Upcoming Pay Plan",
};

// Variants whose timeline includes a resolved (past) segment before the main card.
const PAST_RAIL_VARIANTS = [
  "past-no-current",
  "current-future",
  "past-current",
  "past-current-future",
];

// Variants whose timeline includes a scheduled (future) segment after the main card.
const FUTURE_RAIL_VARIANTS = [
  "current-future",
  "no-current-future",
  "past-current-future",
];

const props = defineProps({
  variant: {
    type: String,
    default: "current",
    validator(value) {
      return [
        "no-current",
        "current",
        "past-no-current",
        "current-future",
        "no-current-future",
        "past-current",
        "past-current-future",
      ].includes(value);
    },
  },
  amountFormatted: {
    type: String,
    default: "$150K",
  },
  basisLabel: {
    type: String,
    default: "USD/year",
  },
  dateRange: {
    type: String,
    default: "Jan 26, 2024 - present",
  },
  startDate: {
    type: String,
    default: "",
  },
  endDate: {
    type: String,
    default: "",
  },
  planReference: {
    type: String,
    default: "",
  },
  /** Version chip, e.g. "V3" (from `current_pay_plan.version_number`). */
  versionLabel: {
    type: String,
    default: "",
  },
  showClose: {
    type: Boolean,
    default: false,
  },
  addLabel: {
    type: String,
    default: "",
  },
  /** Period state vs today: current (green), future (blue), lapsed (grey). */
  state: {
    type: String,
    default: "current",
    validator: (value) => ["current", "future", "lapsed"].includes(value),
  },
  /**
   * Slider: whether a previous / next version exists for the one being shown.
   * `null` (the default) falls back to the variant, so the presentational story
   * and the single-version case keep working unchanged.
   */
  hasPrevious: {
    type: Boolean,
    default: null,
  },
  hasNext: {
    type: Boolean,
    default: null,
  },
  // ER-12691: API-reported block ({ title, description, versionId, ... }). Replaces
  // the + button with the "Not allowed" reason; click emits `blocked-open`.
  blocked: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits([
  "add-click",
  "close-click",
  "plan-click",
  "previous-click",
  "next-click",
  "blocked-open",
]);

function parseDateRange(dateRange) {
  if (!dateRange) {
    return { start: "", end: "" };
  }

  const separatorIndex = dateRange.indexOf(" - ");
  if (separatorIndex === -1) {
    return { start: dateRange.trim(), end: "" };
  }

  return {
    start: dateRange.slice(0, separatorIndex).trim(),
    end: dateRange.slice(separatorIndex + 3).trim(),
  };
}

const parsedDateRange = computed(() => parseDateRange(props.dateRange));

const dateStart = computed(() => props.startDate || parsedDateRange.value.start);

const dateEnd = computed(() => {
  if (props.endDate) return props.endDate;
  return parsedDateRange.value.end;
});

const dateText = computed(() => [dateStart.value, dateEnd.value].filter(Boolean).join(" - "));

const isAddButtonVariant = computed(() =>
  ["no-current", "past-no-current"].includes(props.variant)
);

const isUpcomingLabelVariant = computed(() => props.variant === "no-current-future");

const hasPastRail = computed(() =>
  props.hasPrevious === null
    ? PAST_RAIL_VARIANTS.includes(props.variant)
    : props.hasPrevious
);

const hasFutureRail = computed(() =>
  props.hasNext === null
    ? FUTURE_RAIL_VARIANTS.includes(props.variant)
    : props.hasNext
);

const addLabel = computed(
  () => props.addLabel || ADD_LABELS[props.variant] || "Add Pay Plan"
);
</script>

<style scoped>
.pay-plan-small-card {
  display: flex;
  align-items: center;
  gap: 2px;
  width: 235px;
  max-width: 100%;
}

.pay-plan-small-card--no-current {
  width: 171px;
}

.pay-plan-small-card__arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 26px;
  height: 44px;
  padding: 0;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #f2f4f7;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  cursor: pointer;
}

.pay-plan-small-card__arrow svg {
  display: block;
}

.pay-plan-small-card__arrow:hover {
  background: #eaecf0;
}

.pay-plan-small-card__arrow--past {
  margin-right: 2px;
}

.pay-plan-small-card__arrow--future {
  margin-left: 2px;
}

.pay-plan-small-card__plan {
  display: flex;
  flex-direction: column;
  gap: 6px;
  justify-content: center;
  flex: 1 0 0;
  min-width: 0;
  padding: 4px 4px 6px 7px;
  /* Bar colour = the version's period state vs today (Eric, 2026-10-14):
   * green = current, blue = future, grey = lapsed. */
  background: #d1fadf;
  border: 1px solid #abefc6;
  border-radius: 8px;
  box-shadow: 0 1px 1px rgba(16, 24, 40, 0.05);
  cursor: pointer;
  /* With both ‹ › arrows the plan column is ~141px — never let a row spill out. */
  overflow: hidden;
}

.pay-plan-small-card__plan--future {
  background: #d1e9ff;
  border-color: #b2ddff;
}

.pay-plan-small-card__plan--lapsed {
  background: #f2f4f7;
  border-color: #eaecf0;
}

.pay-plan-small-card--current .pay-plan-small-card__plan,
.pay-plan-small-card--no-current .pay-plan-small-card__plan {
  width: 100%;
}

.pay-plan-small-card__plan-top {
  display: flex;
  align-items: center;
  gap: 7px;
  width: 100%;
  min-width: 0;
}

.pay-plan-small-card__amount,
.pay-plan-small-card__basis {
  min-width: 0;
  overflow: hidden;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #344054;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* The amount is the primary figure — the basis gives way first. */
.pay-plan-small-card__amount {
  flex-shrink: 1;
}

.pay-plan-small-card__basis {
  flex-shrink: 3;
}

.pay-plan-small-card__spacer {
  flex: 1 0 0;
  min-width: 0;
}

.pay-plan-small-card__close {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 2px;
  border: none;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
}

.pay-plan-small-card__close img {
  display: block;
  width: 12px;
  height: 12px;
}

.pay-plan-small-card__version {
  flex-shrink: 0;
  padding: 1px 4px;
  border-radius: 6px;
  background: #ffffff;
  font-family: Inter, sans-serif;
  font-size: 10px;
  font-weight: 600;
  line-height: 16px;
  color: #344054;
}

.pay-plan-small-card__dates-row {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  min-width: 0;
}

.pay-plan-small-card__dates {
  flex: 0 0 auto;
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 10px;
  font-weight: 500;
  line-height: 16px;
  color: #344054;
  white-space: nowrap;
}

.pay-plan-small-card__add {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 37px;
  width: 171px;
  border-radius: 8px;
  box-shadow: 0 1px 1px rgba(16, 24, 40, 0.05);
}

.pay-plan-small-card__add--label-only,
.pay-plan-small-card__add--blocked {
  box-shadow: none;
}

.pay-plan-small-card__add-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-width: 36px;
  min-height: 36px;
  padding: 5px;
  border: 1.059px solid #7f56d9;
  border-radius: 8.471px;
  background: #7f56d9;
  box-shadow: 0 1.059px 2.118px rgba(16, 24, 40, 0.05);
  cursor: pointer;
}

.pay-plan-small-card__add-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.pay-plan-small-card__add-icon img {
  display: block;
  width: 13px;
  height: 13px;
}

.pay-plan-small-card__add-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #344054;
}
</style>
